import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  BarChart3, 
  Files, 
  FileEdit,
  ArrowRight,
  ArrowLeft
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface ResumeLibrarySelectorProps {
  onBack?: () => void;
  onSelectLibrary: (libraryType: 'match-tested' | 'saved' | 'tailored') => void;
}

export function ResumeLibrarySelector({ onBack, onSelectLibrary }: ResumeLibrarySelectorProps) {
  const navigate = useNavigate();
  
  const libraryOptions = [
    {
      id: 'match-tested' as const,
      title: "Match Score Tested Resumes",
      description: "Resumes that have been analyzed against job descriptions with match scores",
      icon: BarChart3,
      color: "from-blue-500 to-blue-600",
      bgColor: "bg-blue-50 hover:bg-blue-100",
      borderColor: "border-blue-200 hover:border-blue-300"
    },
    {
      id: 'saved' as const,
      title: "Saved/Uploaded Resumes",
      description: "Resumes you've uploaded and saved to your library",
      icon: Files,
      color: "from-green-500 to-green-600",
      bgColor: "bg-green-50 hover:bg-green-100",
      borderColor: "border-green-200 hover:border-green-300"
    },
    {
      id: 'tailored' as const,
      title: "Tailored Resumes",
      description: "AI-customized resumes tailored for specific job applications",
      icon: FileEdit,
      color: "from-purple-500 to-purple-600",
      bgColor: "bg-purple-50 hover:bg-purple-100",
      borderColor: "border-purple-200 hover:border-purple-300"
    }
  ];

  return (
    <div className="min-h-screen bg-blue-100 p-6">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg">
        {/* Clean Header with TailorWiz Branding */}
        <div className="bg-gradient-to-r from-accent-light/30 to-primary-light/20 border-b border-border shadow-sm rounded-t-lg backdrop-blur-sm">
          <div className="container mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (onBack) {
                      onBack();
                    } else {
                      navigate('/');
                    }
                  }}
                  className="h-9 w-9 p-0"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="space-y-1">
                  <h1 className="text-2xl font-bold text-foreground">Resume Library</h1>
                  <p className="text-sm text-muted-foreground">Choose which resume library you'd like to access</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full border border-primary/20 shadow-sm">
                <span className="text-xs font-medium text-muted-foreground">powered by</span>
                <span className="text-sm font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">TailorWiz</span>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto py-8 px-6 max-w-3xl">
          <div className="w-full">
            {/* Main Content Area */}
            <div className="flex-1 max-w-full">
              {/* Header Section */}
              <div className="mb-12 text-center">
                <div className="space-y-2 mb-8">
                  <h1 className="text-6xl font-extrabold leading-tight tracking-tight relative">
                    <div className="absolute inset-0 bg-background/60 backdrop-blur-md rounded-lg shadow-lg -z-10"></div>
                    <span className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 dark:from-white dark:via-slate-100 dark:to-white bg-clip-text text-transparent">
                      Resume{" "}
                    </span>
                    <span className="bg-gradient-to-r from-primary via-accent to-primary-hover bg-clip-text text-transparent">
                      Library
                    </span>
                  </h1>
                  
                  <div className="max-w-3xl mx-auto px-4 mt-4 relative">
                    <div className="absolute inset-0 bg-background/50 backdrop-blur-md rounded-lg shadow-md -z-10"></div>
                    <p className="text-lg font-semibold bg-gradient-to-r from-primary/80 to-accent/80 bg-clip-text text-transparent leading-relaxed drop-shadow-lg">
                      Access Your Resume Collections: Match-Tested, Saved & Tailored Documents
                    </p>
                  </div>
                </div>
              </div>

              {/* Library Selection Cards */}
              <div className="space-y-6">
                <div className="grid md:grid-cols-3 gap-6">
                  {libraryOptions.map((option) => {
                    const IconComponent = option.icon;
                    return (
                      <Card 
                        key={option.id}
                        className={`cursor-pointer transition-all duration-200 ${option.borderColor} ${option.bgColor} hover:shadow-lg transform hover:scale-105`}
                        onClick={() => onSelectLibrary(option.id)}
                      >
                        <CardHeader className="text-center pb-4">
                          <div className="mx-auto mb-4">
                            <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${option.color} flex items-center justify-center shadow-lg`}>
                              <IconComponent className="w-8 h-8 text-white" />
                            </div>
                          </div>
                          <CardTitle className="text-xl font-bold">{option.title}</CardTitle>
                        </CardHeader>
                        
                        <CardContent className="text-center">
                          <CardDescription className="text-sm mb-6 min-h-[3rem] flex items-center justify-center">
                            {option.description}
                          </CardDescription>
                          
                          <Button 
                            variant="outline" 
                            className="w-full group"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectLibrary(option.id);
                            }}
                          >
                            View Library
                            <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}